import type { ExportRedeemCodesResp, MemberTypeConfig } from '@shared/api.interface';

export type ExportFormat = 'txt' | 'csv';

const csvCell = (value: string) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/** TXT 一行一个码（发货软件卡密导入格式）；CSV 带表头，加 BOM 方便 Excel 识别中文 */
export function buildExportFile(result: ExportRedeemCodesResp, format: ExportFormat): { name: string; content: string; mime: string } {
  const date = new Date().toISOString().slice(0, 10);
  const base = `${result.product_label}-批次${result.batch_id}-${date}`.replace(/[\\/:*?"<>|\s]+/g, '_');
  if (format === 'txt') {
    return { name: `${base}.txt`, content: result.codes.join('\r\n') + (result.codes.length ? '\r\n' : ''), mime: 'text/plain;charset=utf-8' };
  }
  const rows = [['兑换码', '会员类型', '批次'], ...result.codes.map(code => [code, result.member_type, `#${result.batch_id} ${result.product_label}`])];
  return { name: `${base}.csv`, content: '﻿' + rows.map(row => row.map(csvCell).join(',')).join('\r\n') + '\r\n', mime: 'text/csv;charset=utf-8' };
}

export function downloadTextFile(file: { name: string; content: string; mime: string }) {
  const url = URL.createObjectURL(new Blob([file.content], { type: file.mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 按表头猜测哪一列是订单号、哪一列是卡密 */
export function guessColumns(header: string[]): { orderColumn: number; codeColumn: number } {
  const find = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const index = header.findIndex(cell => pattern.test(cell));
      if (index >= 0) return index;
    }
    return -1;
  };
  const orderColumn = find([/订单(编号|号)/, /订单/, /order/i]);
  let codeColumn = find([/卡密/, /兑换码/, /卡号/, /code/i]);
  if (codeColumn === orderColumn) codeColumn = -1;
  return { orderColumn, codeColumn };
}

export function columnLabel(index: number, header: string[]): string {
  let letters = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  }
  return `${letters} 列${header[index] ? `：${header[index]}` : ''}`;
}

export function describeMemberType(config: MemberTypeConfig | undefined): string {
  if (!config) return '';
  const quota = config.quota_mode === 'total'
    ? `总次数 ${config.total_quota}`
    : `每月 ${config.monthly_quota} 次`;
  return `${quota} · 单日上限 ${config.daily_limit > 0 ? config.daily_limit : '不限'} · 激活后 ${config.validity_months} 个月有效`;
}

/** 总次数模式下单日上限小于总次数时，用户没法一天领完，生成前提醒 */
export function dailyLimitWarning(config: MemberTypeConfig | undefined): string {
  if (!config || config.quota_mode !== 'total') return '';
  if (config.daily_limit > 0 && config.daily_limit < config.total_quota) {
    return `这个类型单日最多领 ${config.daily_limit} 个，用户不能一天领完 ${config.total_quota} 个。需要的话先到“会员类型”里把单日上限改成 ${config.total_quota} 或 0（不限）。`;
  }
  return '';
}
